/**
 * "Where did this come from?" — opened from a citation chip.
 *
 * Data sources show the exact rows the assistant was given (first 25),
 * the filters in plain English, the data-through date and the file the
 * data was loaded from. Note sources show the visit note itself.
 */

import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box, Button, Chip, Dialog, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import type { ChatCitation } from '../../api/chat';
import {
  COMPACT_QUERY,
  DIALOG_Z,
  FONT_SANS,
  ACCENT,
  ACCENT_SOFT,
  INK,
  LINE,
  MUTED,
  SURFACE,
  TEXT,
  FS,
} from './chatTheme';

interface Props {
  source: ChatCitation | null;
  number: number | null;
  onClose: () => void;
}

const COLUMN_LABELS: Record<string, string> = {
  six_packs: '6-packs',
  six_packs_a: 'Period A',
  six_packs_b: 'Period B',
  change_six_packs: 'Change',
  lifetime_six_packs: 'Lifetime 6-pk',
  total_six_packs: 'Total 6-pk',
  change_pct: 'Change %',
  share_of_total_pct: 'Share %',
  share_of_notes_pct: 'Share %',
  accounts_ordering: 'Accounts',
  last_month_ordered: 'Last ordered',
  distinct_accounts: 'Accounts',
  outcome_ordered: 'Ordered',
  latest_visit_date: 'Latest visit',
};

const HIDDEN_COLUMNS = new Set(['account_id', 'product_id', 'rep_id', 'note_id', 'cite_as']);
/** 9L stays the wire unit; like the rest of the dashboard, only its 6-pack twin is shown. */
const isHidden = (col: string) => HIDDEN_COLUMNS.has(col) || col.includes('cases_9l');

function label(col: string): string {
  return COLUMN_LABELS[col] ?? col.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function SourceDialog({ source, number, onClose }: Props) {
  const navigate = useNavigate();
  const compact = useMediaQuery(COMPACT_QUERY);
  const open = source !== null;
  const isNote = source?.kind === 'note';
  const columns = (source?.columns ?? []).filter((c) => !isHidden(c));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={compact}
      sx={{ zIndex: DIALOG_Z }}
      PaperProps={{
        sx: {
          borderRadius: compact ? 0 : 3,
          bgcolor: '#fff',
          fontFamily: FONT_SANS,
          border: `1px solid ${LINE}`,
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.18)',
        },
      }}
    >
      {source && (
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: SURFACE,
                color: INK,
                flexShrink: 0,
              }}
            >
              {isNote ? <EventNoteOutlinedIcon /> : <TableChartOutlinedIcon />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: FS.micro,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: MUTED,
                }}
              >
                Source {number ?? ''} · {isNote ? 'Visit note' : 'Depletions data'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_SANS,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: INK,
                  letterSpacing: '-0.01em',
                }}
              >
                {source.title}
              </Typography>
              <Typography sx={{ fontSize: FS.sm, color: MUTED, mt: 0.25 }}>
                {source.detail}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Close" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {isNote && source.note ? (
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Chip size="small" label={formatDate(source.note.visit_date)} />
                <Chip size="small" label={source.note.rep} />
                <Chip
                  size="small"
                  label={source.note.channel}
                  sx={{ textTransform: 'capitalize' }}
                />
                <Chip
                  size="small"
                  label={source.note.outcome.replace(/_/g, ' ')}
                  sx={{ textTransform: 'capitalize', bgcolor: ACCENT_SOFT, color: ACCENT }}
                />
                {source.note.products_discussed.map((p) => (
                  <Chip key={p} size="small" variant="outlined" label={p} />
                ))}
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderLeft: `3px solid ${ACCENT}`,
                  bgcolor: SURFACE,
                  fontSize: '0.92rem',
                  lineHeight: 1.7,
                  color: TEXT,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {source.note.text}
              </Box>
              <Typography sx={{ fontSize: FS.table, color: MUTED, mt: 1 }}>
                {source.note.account}
                {source.note.city ? ` · ${source.note.city}` : ''}
                {source.note.state ? `, ${source.note.state}` : ''}
              </Typography>
            </Box>
          ) : (
            <>
              {source.rows.length > 0 ? (
                <Box
                  sx={{
                    border: `1px solid ${LINE}`,
                    borderRadius: 2,
                    overflow: 'auto',
                    maxHeight: compact ? 'none' : 380,
                    bgcolor: '#fff',
                  }}
                >
                  <Box
                    component="table"
                    sx={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: FS.table,
                      fontVariantNumeric: 'tabular-nums',
                      '& th': {
                        position: 'sticky',
                        top: 0,
                        textAlign: 'left',
                        bgcolor: SURFACE,
                        color: MUTED,
                        fontWeight: 600,
                        fontSize: FS.micro,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        px: 1.5,
                        py: 1,
                        whiteSpace: 'nowrap',
                      },
                      '& td': {
                        px: 1.5,
                        py: 0.85,
                        borderTop: `1px solid ${LINE}`,
                        color: INK,
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        {columns.map((c) => (
                          <th key={c}>{label(c)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {source.rows.map((row, i) => (
                        <tr key={i}>
                          {columns.map((c) => (
                            <td key={c}>{formatCell(row[c])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontSize: FS.body, color: MUTED }}>
                  No rows matched these filters.
                </Typography>
              )}
              {source.row_count > source.rows.length && (
                <Typography sx={{ fontSize: FS.label, color: MUTED, mt: 0.75 }}>
                  Showing {source.rows.length} of {source.row_count.toLocaleString()} rows.
                </Typography>
              )}
            </>
          )}

          <Box
            sx={{
              mt: 2.5,
              pt: 2,
              borderTop: `1px solid ${LINE}`,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            {source.data_through && (
              <Typography sx={{ fontSize: FS.label, color: MUTED }}>
                Data through <b>{formatDate(source.data_through)}</b>
              </Typography>
            )}
            {source.files[0] && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MUTED }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />
                <Typography sx={{ fontSize: FS.label }}>
                  {source.files[0].filename}
                  {source.files[0].loaded_at
                    ? ` · loaded ${formatDate(source.files[0].loaded_at)}`
                    : ''}
                </Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            {source.link && (
              <Button
                size="small"
                endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  onClose();
                  navigate(source.link as string);
                }}
                sx={{ color: INK, fontWeight: 600 }}
              >
                {isNote ? 'Open account' : 'Open in dashboard'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
